/**
 * Pack Builder JavaScript
 * State management for meal selection and cart integration
 *
 * Features:
 * - URL param reading (?pack=handle)
 * - Pack product data fetching
 * - Selection tracking with quantity
 * - Counter and progress updates
 * - Line filtering
 * - Cart API integration with bundle properties
 * - Mobile drawer toggle
 */

(function () {
  "use strict";

  // State
  const state = {
    packHandle: null,
    packCode: null,
    packVariantId: null,
    mealsCount: 0,
    allowedLines: [],
    selected: new Map(), // variantId -> { qty, title }
    editingInstanceId: null, // For edit mode
  };

  // Variant ID -> Card Element Lookup
  const variantLookup = new Map();

  /**
   * Index all cards for fast lookup by Variant ID
   */
  function indexCards() {
    variantLookup.clear();
    elements.mealCards.forEach((card) => {
      const variants = getVariantsMap(card);
      variants.forEach((v) => {
        variantLookup.set(v.id, card);
      });
    });
    console.log(
      `[PackBuilder] Indexed ${variantLookup.size} variants from ${elements.mealCards.length} cards.`
    );
  }

  // DOM Elements
  const elements = {
    section: document.querySelector("[data-meal-picker]"),
    mealsGrid: document.querySelector("[data-meals-grid]"),
    mealCards: document.querySelectorAll("[data-meal-card]"),
    filters: document.querySelectorAll("[data-filter]"),

    // Counters
    mealsRequiredText: document.querySelector("[data-meals-required]"),
    selectedCountDesktop: document.querySelector("[data-selected-count]"),
    requiredCountDesktop: document.querySelector("[data-required-count]"),
    selectedCountMobile: document.querySelector("[data-selected-count-mobile]"),
    requiredCountMobile: document.querySelector("[data-required-count-mobile]"),
    selectedCountDrawer: document.querySelector("[data-selected-count-drawer]"),
    requiredCountDrawer: document.querySelector("[data-required-count-drawer]"),

    // Progress bars
    progressBarDesktop: document.querySelector("[data-progress-bar]"),
    progressBarMobile: document.querySelector("[data-progress-bar-mobile]"),

    // Lists
    selectedListDesktop: document.querySelector("[data-selected-list]"),
    selectedListMobile: document.querySelector("[data-selected-list-mobile]"),

    // CTAs
    addToCartDesktop: document.querySelector("[data-add-to-cart]"),
    addToCartMobile: document.querySelector("[data-add-to-cart-mobile]"),

    // Messages
    validationMessageDesktop: document.querySelector(
      "[data-validation-message]"
    ),
    validationMessageMobile: document.querySelector(
      "[data-validation-message-mobile]"
    ),

    // Mobile drawer
    drawer: document.querySelector("[data-drawer]"),
    toggleDrawerBtn: document.querySelector("[data-toggle-drawer]"),
    closeDrawerBtns: document.querySelectorAll("[data-close-drawer]"),
    mobileBar: document.querySelector("[data-summary-mobile]"),
  };

  /**
   * Helper to parse variants JSON from DOM
   */
  function getVariantsMap(card) {
    try {
      const json = card.dataset.variants;
      return json ? JSON.parse(json) : [];
    } catch (e) {
      console.warn("Error parsing variants", e);
      return [];
    }
  }

  /**
   * Find matching variant for current context
   */
  /**
   * Find matching variant for current context
   */
  function findMatchingVariant(card) {
    const variants = getVariantsMap(card);
    // Normalize helpers
    const norm = (str) => (str || "").toLowerCase().trim();
    const targetW = norm(state.targetWeight);
    const targetT = norm(state.targetType);

    // 1. Try exact match on Weight & Type
    let match = variants.find((v) => {
      // Support both Shopify standard keys and minified keys from meal-picker
      const opts = [
        v.option1 || v.op1,
        v.option2 || v.op2,
        v.option3 || v.op3,
      ].map(norm);
      return opts.includes(targetW) && opts.includes(targetT);
    });

    // Debugging match failure
    if (!match) {
      console.warn(
        `[PackBuilder] Match failed for '${card.dataset.productTitle}'. Looking for Weight: '${targetW}', Type: '${targetT}'`
      );
    }

    // 2. Fallback: If product has no "Pack" variants, maybe it's a legacy product?
    // User strategy implies mixed catalog.
    // IF no match found, we should probably BLOCK or warn?
    // For safety during migration: fallback to the first variant or the card's native data-variant-id
    if (!match) {
      // Only if we absolutely can't find a match, use the default
      const defaultId = parseInt(card.dataset.variantId, 10);
      match = variants.find((v) => v.id === defaultId) || { id: defaultId };
      console.warn(
        "No specific pack variant found for",
        card.dataset.productTitle,
        "- using default",
        match
      );
    }

    return match;
  }

  /**
   * Initialize the pack builder
   */
  async function init() {
    if (!elements.section) return;

    // Read variant ID from URL
    // Read params
    const urlParams = new URLSearchParams(window.location.search);
    const variantIdParam = urlParams.get("id");
    const packCodeParam = urlParams.get("pack_code");

    // Get configuration
    const packConfigEl = document.querySelector("[data-pack-config]");
    if (!packConfigEl) {
      showError("Erro de configuração: dados do pack não encontrados.");
      return;
    }

    let config, variants;
    try {
      config = JSON.parse(packConfigEl.textContent);
      variants = config.variants || [];
    } catch (e) {
      showError("Erro ao ler configuração do pack.");
      return;
    }

    // Resolve Variant ID
    if (variantIdParam) {
      state.packVariantId = parseInt(variantIdParam, 10);
    } else if (packCodeParam) {
      // Find variant by code
      const found = variants.find((v) => v.packCode === packCodeParam);
      if (found) {
        state.packVariantId = found.id;
      } else {
        showError(`Pack com código ${packCodeParam} não encontrado.`);
        return;
      }
    } else {
      showError("Nenhum pack selecionado. Por favor, volte e escolha um pack.");
      return;
    }

    // Find the selected variant object
    const selectedVariant = variants.find((v) => v.id === state.packVariantId);

    if (!selectedVariant) {
      showError("Variante do pack não encontrada.");
      return;
    }

    state.packVariant = selectedVariant;

    // Set state from variant data
    state.mealsCount = state.packVariant.mealsCount;
    state.packCode = state.packVariant.packCode;
    state.allowedLines = state.packVariant.allowedLines || [];

    // Fallback parsing for Count if missing
    if (!state.mealsCount) {
      // Try to find "X Refeições" in title
      let match = state.packVariant.title.match(/(\d+)\s*Refeiç/i);

      if (!match) {
        // Try to find integer NOT followed by "g" or "kg"
        // This prevents matching "300g" as 300 meals
        match = state.packVariant.title.match(/(\d+)(?!\s*(g|kg))/i);
      }
      state.mealsCount = match ? parseInt(match[1], 10) : 0;
    }

    if (!state.packCode) {
      state.packCode = `PEC-${state.mealsCount}`;
    }

    // DERIVE WEIGHT & TYPE CONTEXT
    // We scan the options to see if we find a weight ("g" or "kg") and a Pack Type
    const opts = [
      state.packVariant.option1,
      state.packVariant.option2,
      state.packVariant.option3,
    ];

    // 1. Find Weight (e.g. 300g)
    state.targetWeight = opts.find(
      (o) => o && (o.includes("g") || o.includes("kg"))
    ); // Naive heuristic

    if (!state.targetWeight) {
      const titleMatch = state.packVariant.title.match(/(\d+(g|kg))/);
      if (titleMatch) state.targetWeight = titleMatch[0];
    }

    // 2. Find Type (e.g. Pack 10)
    // First look for explicitly "Pack"
    state.targetType = opts.find((o) => o && o.toLowerCase().includes("pack"));

    // If not found, look for "Refeições" (e.g. "10 Refeições") and map to "Pack 10"
    if (!state.targetType) {
      const refeicoesOpt = opts.find(
        (o) => o && o.toLowerCase().includes("refeiç")
      );
      if (refeicoesOpt) {
        // Extract number from "10 Refeições"
        const numMatch = refeicoesOpt.match(/(\d+)/);
        if (numMatch) {
          state.targetType = `Pack ${numMatch[1]}`;
        }
      }
    }

    // Fallback
    if (!state.targetType) {
      state.targetType = `Pack ${state.mealsCount}`;
    }

    console.log("Context Initialized:", {
      count: state.mealsCount,
      weight: state.targetWeight,
      type: state.targetType,
    });

    // Update UI with pack info
    // Update UI with pack info
    updatePackInfo();

    // Index cards for fast lookup
    indexCards();

    // Rehydrate state if editing
    await rehydrateState();

    // Bind event listeners
    bindEvents();

    // Initial UI update
    updateUI();

    // Expose state for debugging
    window.packBuilderState = state;
  }

  // fetchPackData is no longer needed as we use liquid injection

  /**
   * Update pack info in UI
   */
  function updatePackInfo() {
    if (elements.mealsRequiredText) {
      elements.mealsRequiredText.textContent = state.mealsCount;
    }

    // Update all required count displays
    const requiredDisplays = [
      elements.requiredCountDesktop,
      elements.requiredCountMobile,
      elements.requiredCountDrawer,
    ];

    requiredDisplays.forEach((el) => {
      if (el) el.textContent = state.mealsCount;
    });
  }

  /**
   * Bind event listeners
   */
  function bindEvents() {
    // Meal card quantity buttons
    elements.mealCards.forEach((card) => {
      const increaseBtn = card.querySelector('[data-action="increase"]');
      const decreaseBtn = card.querySelector('[data-action="decrease"]');

      if (increaseBtn) {
        increaseBtn.addEventListener("click", () =>
          handleQuantityChange(card, 1)
        );
      }

      if (decreaseBtn) {
        decreaseBtn.addEventListener("click", () =>
          handleQuantityChange(card, -1)
        );
      }
    });

    // Line filters
    elements.filters.forEach((filter) => {
      filter.addEventListener("click", () => handleFilterChange(filter));
    });

    // Add to cart buttons
    if (elements.addToCartDesktop) {
      elements.addToCartDesktop.addEventListener("click", handleAddToCart);
    }

    if (elements.addToCartMobile) {
      elements.addToCartMobile.addEventListener("click", handleAddToCart);
    }

    // Mobile drawer
    if (elements.toggleDrawerBtn) {
      elements.toggleDrawerBtn.addEventListener("click", openDrawer);
    }

    elements.closeDrawerBtns.forEach((btn) => {
      btn.addEventListener("click", closeDrawer);
    });
  }

  /**
   * Handle quantity change for a meal
   */
  function handleQuantityChange(card, delta) {
    // 1. Resolve which actual Variant we are adding/removing
    const variantMatch = findMatchingVariant(card);
    const variantId = variantMatch.id;
    const title = card.dataset.productTitle;

    // Check current qty
    const currentItem = state.selected.get(variantId) || { qty: 0 };
    const newQty = Math.max(0, currentItem.qty + delta);

    // Validation: Max Meals
    const totalSelected = getTotalSelected();

    // If adding
    if (delta > 0) {
      if (totalSelected >= state.mealsCount) {
        showValidationMessage(
          `O pack já está cheio (${state.mealsCount} refeições).`
        );
        return;
      }
    }

    if (newQty === 0) {
      state.selected.delete(variantId);
    } else {
      state.selected.set(variantId, {
        qty: newQty,
        title: title,
      });
    }

    // --- Tracking: MealAdded (direct fbq call) ---
    if (delta > 0 && newQty > 0) {
      try {
        const updatedTotal = getTotalSelected();
        const mealCategory = card.dataset.line || "equilibrio";
        fbq("trackCustom", "MealAdded", {
          content_name: title,
          content_category: mealCategory,
          pack_size: String(state.mealsCount),
          portion_size: state.targetWeight || "300g",
          slots_filled: String(updatedTotal),
          slots_total: String(state.mealsCount)
        });
        console.log("[PackBuilder] MealAdded fired", title, updatedTotal + "/" + state.mealsCount);
      } catch (err) {
        console.warn("[PackBuilder] MealAdded failed", err);
      }
    }

    // UI Updates
    updateCardUI(card, newQty);
    updateUI();
  }

  /**
   * Update a card's UI based on quantity
   */
  function updateCardUI(card, qty) {
    const qtyDisplay = card.querySelector("[data-qty]");
    const decreaseBtn = card.querySelector('[data-action="decrease"]');

    if (qtyDisplay) qtyDisplay.textContent = qty;

    if (decreaseBtn) {
      decreaseBtn.disabled = qty === 0;
    }

    card.classList.toggle("is-selected", qty > 0);
  }

  /**
   * Handle line filter change
   */
  function handleFilterChange(activeFilter) {
    const line = activeFilter.dataset.filter;

    // Update active state
    elements.filters.forEach((f) => {
      f.classList.toggle("is-active", f === activeFilter);
      f.setAttribute("aria-pressed", f === activeFilter ? "true" : "false");
    });

    // Filter cards
    elements.mealCards.forEach((card) => {
      const cardLine = card.dataset.line;
      const show = line === "all" || cardLine === line;
      card.dataset.hidden = !show;
    });
  }

  /**
   * Get total number of selected meals
   */
  function getTotalSelected() {
    let total = 0;
    state.selected.forEach((item) => {
      total += item.qty;
    });
    return total;
  }

  /**
   * Update all UI elements
   */
  function updateUI() {
    const totalSelected = getTotalSelected();
    const progress = Math.min(100, (totalSelected / state.mealsCount) * 100);
    const isComplete = totalSelected === state.mealsCount;
    const remaining = state.mealsCount - totalSelected;

    // Update counters
    const countDisplays = [
      elements.selectedCountDesktop,
      elements.selectedCountMobile,
      elements.selectedCountDrawer,
    ];

    countDisplays.forEach((el) => {
      if (el) el.textContent = totalSelected;
    });

    // Update progress bars
    const progressScale = progress / 100;
    if (elements.progressBarDesktop) {
      elements.progressBarDesktop.style.setProperty("--progress-scale", progressScale);
    }
    if (elements.progressBarMobile) {
      elements.progressBarMobile.style.setProperty("--progress-scale", progressScale);
    }

    // Update selected lists
    updateSelectedList(elements.selectedListDesktop);
    updateSelectedList(elements.selectedListMobile);

    // Update CTAs
    if (elements.addToCartDesktop) {
      elements.addToCartDesktop.disabled = !isComplete;
    }
    if (elements.addToCartMobile) {
      elements.addToCartMobile.disabled = !isComplete;
    }

    // Update validation messages
    let message = "";
    if (totalSelected === 0) {
      message = "";
    } else if (remaining > 0) {
      message =
        remaining === 1 ? "Falta 1 refeição" : `Faltam ${remaining} refeições`;
    } else {
      message = "✓ Pack completo!";
    }

    if (elements.validationMessageDesktop) {
      elements.validationMessageDesktop.textContent = message;
      elements.validationMessageDesktop.style.color = isComplete
        ? "var(--color-success)"
        : "var(--color-accent)";
    }
    if (elements.validationMessageMobile) {
      elements.validationMessageMobile.textContent = message;
      elements.validationMessageMobile.style.color = isComplete
        ? "var(--color-success)"
        : "var(--color-accent)";
    }
  }

  /**
   * Update selected list
   */
  function updateSelectedList(listElement) {
    if (!listElement) return;

    if (state.selected.size === 0) {
      listElement.innerHTML =
        '<li class="summary-panel__empty drawer__empty">Nenhuma refeição selecionada</li>';
      return;
    }

    let html = "";
    state.selected.forEach((item, variantId) => {
      html += `
        <li class="summary-panel__item drawer__item">
          <span>${item.title}</span>
          <strong>×${item.qty}</strong>
        </li>
      `;
    });

    listElement.innerHTML = html;
  }

  /**
   * Show validation message temporarily
   */
  function showValidationMessage(message) {
    if (elements.validationMessageDesktop) {
      elements.validationMessageDesktop.textContent = message;
    }
    if (elements.validationMessageMobile) {
      elements.validationMessageMobile.textContent = message;
    }

    setTimeout(() => updateUI(), 2000);
  }

  /**
   * Show error state
   */
  function showError(message) {
    if (elements.section) {
      elements.section.innerHTML = `
        <div class="container" style="padding: 4rem 1rem; text-align: center;">
          <p style="color: var(--color-error);">${message}</p>
          <a href="/pages/montar-pack" class="btn btn--secondary" style="margin-top: 1rem;">
            Voltar aos packs
          </a>
        </div>
      `;
    }
  }

  /**
   * Open mobile drawer
   */
  function openDrawer() {
    if (elements.drawer) {
      elements.drawer.hidden = false;
      document.body.style.overflow = "hidden";
    }
  }

  /**
   * Close mobile drawer
   */
  function closeDrawer() {
    if (elements.drawer) {
      elements.drawer.hidden = true;
      document.body.style.overflow = "";
    }
  }

  /**
   * Helper to set loading state on buttons
   */
  function setLoading(isLoading) {
    if (elements.addToCartDesktop) {
      elements.addToCartDesktop.disabled = isLoading;
      elements.addToCartDesktop.textContent = isLoading
        ? "Adicionando..."
        : "Adicionar ao Carrinho";
    }
    if (elements.addToCartMobile) {
      elements.addToCartMobile.disabled = isLoading;
      elements.addToCartMobile.textContent = isLoading
        ? "Adicionando..."
        : "Adicionar ao Carrinho";
    }
  }

  /**
   * Rehydrate state from URL params (Edit Mode)
   */
  /**
   * Rehydrate state from URL params (Edit Mode)
   */
  async function rehydrateState() {
    const urlParams = new URLSearchParams(window.location.search);
    const editId = urlParams.get("edit");
    const itemsParam = urlParams.get("items");

    if (editId && itemsParam) {
      console.log("[PackBuilder] Rehydrating Edit Mode:", editId, itemsParam);
      state.editingInstanceId = editId;

      try {
        const pairs = itemsParam.split(",");
        pairs.forEach((pair) => {
          if (!pair) return;
          const [idStr, qtyStr] = pair.split(":");
          const id = parseInt(idStr, 10);
          const qty = parseInt(qtyStr, 10);

          if (id && qty > 0) {
            let title = `Prato ${id}`;
            const card = variantLookup.get(id);

            if (card) {
              title = card.dataset.productTitle;
              console.log(
                `[PackBuilder] Restored: ${title} (ID: ${id}, Qty: ${qty})`
              );
              updateCardUI(card, qty);
            } else {
              console.warn(
                `[PackBuilder] Card not found for Variant ID: ${id}`
              );
            }

            state.selected.set(id, { qty, title });
          }
        });

        // Update Button Text
        const btnText = "Atualizar Pack";
        if (elements.addToCartDesktop)
          elements.addToCartDesktop.textContent = btnText;
        if (elements.addToCartMobile)
          elements.addToCartMobile.textContent = btnText;
      } catch (e) {
        console.error("[PackBuilder] Error rehydrating:", e);
      }
    }
  }

  /**
   * Handle add to cart (Insert or Update)
   */
  async function handleAddToCart() {
    const totalSelected = getTotalSelected();

    if (totalSelected !== state.mealsCount) {
      showValidationMessage(
        `Por favor escolha exatamente ${state.mealsCount} refeições.`
      );
      return;
    }

    // --- Tracking: PackComplete (direct fbq call) ---
    try {
      const mealNames = [];
      state.selected.forEach((item) => {
        for (let i = 0; i < item.qty; i++) {
          mealNames.push(item.title);
        }
      });

      // Determine product_line from pack variant options
      const packOpts = [
        state.packVariant.option1,
        state.packVariant.option2,
        state.packVariant.option3,
      ].filter(Boolean).map(o => o.toLowerCase());

      let productLine = "equilibrio";
      if (packOpts.some(o => o.includes("low carb") || o.includes("low_carb"))) {
        productLine = "low_carb";
      } else if (packOpts.some(o => o.includes("tradicional"))) {
        productLine = "tradicional";
      } else if (packOpts.some(o => o.includes("descoberta"))) {
        productLine = "descoberta";
      }

      fbq("trackCustom", "PackComplete", {
        content_name: "Pack " + state.mealsCount + " completo",
        content_category: productLine,
        value: state.packVariant.price || 0,
        currency: "EUR",
        pack_size: String(state.mealsCount),
        portion_size: state.targetWeight || "300g",
        product_line: productLine,
        num_items: String(mealNames.length),
        meals: mealNames.join(", ")
      });
      console.log("[PackBuilder] PackComplete fired", { pack_size: state.mealsCount, meals: mealNames.length });
    } catch (err) {
      console.warn("[PackBuilder] PackComplete failed", err);
    }

    setLoading(true);

    // Determine Instance ID
    // If editing, keep same ID. If new, generate timestamp.
    const instanceId = state.editingInstanceId || Date.now().toString();
    const selectedMealsSummary = [];
    state.selected.forEach((item, variantId) => {
      if (item.qty > 0) {
        selectedMealsSummary.push({
          mealId: String(variantId),
          variantId: String(variantId),
          title: item.title,
          quantity: item.qty,
        });
      }
    });
    const selectedMealsJson = JSON.stringify(selectedMealsSummary);

    // PREPARE NEW PAYLOAD
    const itemsToAdd = [];
    state.selected.forEach((item, variantId) => {
      if (item.qty > 0) {
        itemsToAdd.push({
          id: variantId,
          quantity: item.qty,
          properties: {
            _source: "pack_builder",
            _pack_id: state.packCode,
            _pack_size: String(state.mealsCount),
            _pack_instance_id: instanceId, // UNIQUE GROUPING KEY
            _portion_size: state.targetWeight || "",
            _selection_completed: "true",
            _selected_meals: selectedMealsJson,
          },
        });
      }
    });

    try {
      // IF EDITING: First remove old items
      if (state.editingInstanceId) {
        console.log("[PackBuilder] Updating pack. Removing old items...");
        // Fetch cart to find items with this instance ID
        const cartRes = await fetch(window.Shopify.routes.root + "cart.js");
        const cart = await cartRes.json();

        const updates = {};
        let hasChanges = false;

        cart.items.forEach((item) => {
          if (
            item.properties &&
            item.properties._pack_instance_id === state.editingInstanceId
          ) {
            updates[item.key] = 0; // Set quantity to 0 to remove
            hasChanges = true;
          }
        });

        if (hasChanges) {
          await fetch(window.Shopify.routes.root + "cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ updates }),
          });
          console.log("[PackBuilder] Old items removed.");
        }
      }

      // ADD NEW ITEMS
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: itemsToAdd }),
      });

      const data = await response.json();

      if (response.ok) {
        window.location.href = "/cart";
      } else {
        showError(data.description || "Erro ao adicionar ao carrinho");
      }
    } catch (error) {
      showError("Erro de conexão");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
